// src/redux/modules/auth/services/authApi.slice.ts
import { baseApi } from '@/rtkQuery/features/services/baseApi';
import { AUTH_ENDPOINTS } from '@/rtkQuery/config/authApi.config';
import { buildQuery, handleAuthError } from '@/rtkQuery/lib';
import type {
    ChangePasswordRequest,
    ChangePasswordResponse,
    LoginRequest,
    LoginResponse,
    RefreshRequest,
    RefreshResponse,
    RegisterRequest,
    RegisterResponse,
    ResetPasswordResponse,
} from '@/rtkQuery/features/models/auth.model';

export const authApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        login: builder.mutation<LoginResponse, LoginRequest>({
            query: buildQuery<LoginRequest>(AUTH_ENDPOINTS.LOGIN, 'POST'),
            transformResponse: (response: LoginResponse, meta) => {
                (meta as any).status = 'success';
                (meta as any).error = null;
                return response;
            },
            transformErrorResponse: (response: any, meta) => {

                const errorMessage = handleAuthError({
                    message: response.data || response.data?.message,
                    status: response.status,
                });
                (meta as any).status = 'error';
                (meta as any).error = errorMessage;
                return {
                    status: response.status,
                    message: errorMessage,
                    errors: response.data || response.data?.errors || {},
                };
            },
            invalidatesTags: ['Auth'],
        }),

        refreshToken: builder.mutation<RefreshResponse, RefreshRequest>({
            query: buildQuery<RefreshRequest>(AUTH_ENDPOINTS.REFRESH, 'POST'),
            transformResponse: (response: RefreshResponse, meta) => {
                (meta as any).status = 'success';
                (meta as any).error = null;
                return response;
            },
            transformErrorResponse: (response: any, meta) => {
                const errorMessage = handleAuthError({
                    message: response.data?.message,
                    status: response.status,
                });
                (meta as any).status = 'error';
                (meta as any).error = errorMessage;
                return {
                    status: response.status,
                    message: errorMessage,
                    errors: response.data?.errors || {},
                };
            },
            invalidatesTags: ['Auth'],
        }),

        register: builder.mutation<RegisterResponse, RegisterRequest>({
            query: buildQuery<RegisterRequest>(AUTH_ENDPOINTS.REGISTER, 'POST'),
            transformResponse: (response: any, meta) => {
                (meta as any).status = 'success';
                (meta as any).error = null;

                return {
                    AccessToken: response.AccessToken,
                    RefreshToken: response.RefreshToken,
                    message: response.message,
                    user: response.user,
                };
            },
            transformErrorResponse: (response: any, meta) => {
                const errorDescription = response?.data?.Errors?.[0]?.Description;
                const fallbackMessage = response?.data?.Message;

                const errorMessage = handleAuthError({
                    message: errorDescription || fallbackMessage,
                    status: response.status,
                });

                (meta as any).status = 'error';
                (meta as any).error = errorMessage;

                return {
                    status: response.status,
                    message: errorMessage,
                    errors: response?.data?.Errors || [],
                };
            },

            invalidatesTags: ['Auth'],
        }),

        changePassword: builder.mutation<ChangePasswordResponse, ChangePasswordRequest>({
            query: buildQuery<ChangePasswordRequest>(AUTH_ENDPOINTS.CHANGE_PASSWORD, 'POST'),
            transformResponse: (response: ChangePasswordResponse, meta) => {
                (meta as any).status = 'success';
                (meta as any).error = null;
                return response;
            },
            transformErrorResponse: (response: any, meta) => {
                const errorMessage = handleAuthError({
                    message: response?.data?.Message || "Something went wrong while changing password",
                    status: response.status,
                });
                (meta as any).status = 'error';
                (meta as any).error = errorMessage;

                return {
                    status: response.status,
                    message: errorMessage,
                    errors: response?.data?.Errors || [],
                };
            },
            invalidatesTags: ['Auth'],
        }),
        resetPassword: builder.mutation<
            ResetPasswordResponse,
            { token: string; email: string; newPassword: string; confirmPassword: string }
        >({
            query: ({ token, email, newPassword, confirmPassword }) => ({
                url: `${AUTH_ENDPOINTS.RESET_PASSWORD}?token=${encodeURIComponent(token)}`,
                method: 'POST',
                body: {
                    email,
                    newPassword,
                    confirmPassword,
                },
            }),
            transformResponse: (response: any, meta) => {
                (meta as any).status = 'success';
                return response;
            },
            transformErrorResponse: (response: any, meta) => {
                const errorDescription = response?.data?.Errors?.[0]?.Description;
                const fallbackMessage = response?.data?.Message;
                const errorMessage = handleAuthError({
                    message: fallbackMessage || errorDescription,
                    status: response.status,
                });
                (meta as any).status = 'error';
                (meta as any).error = errorMessage;
                return {
                    status: response.status,
                    message: errorMessage,
                    errors: response?.data?.Errors || [],
                };
            },
            invalidatesTags: ['Auth'],
        }),



    }),
    overrideExisting: false,
});

export const {
    useLoginMutation,
    useRefreshTokenMutation,
    useRegisterMutation,
    useChangePasswordMutation,
    useResetPasswordMutation,
} = authApi;
